import { BREADCRUMB_MODEL_NAME } from '@/components/ui/Breadcrumbs/Breadcrumbs';
import { BreadcrumbComponent, BreadcrumbContent, BreadcrumbInputReference, BreadcrumbItem } from '@/types/breadcrumb';
import { builder } from '@builder.io/sdk';
import { cache } from 'react';

/* 
  This is used to get the Breadcrumb model content for a given BuilderReference id.
  Wrapped in cache() to deduplicate calls within the same render pass.
*/
const getBreadcrumbContent = cache((id: string): Promise<BreadcrumbContent> => {
  return builder
    .get(BREADCRUMB_MODEL_NAME, {
      query: {
        id,
      },
      options: { noTargeting: true, includeRefs: true },
    })
    .toPromise();
});

/*
  Takes a BuilderElement and returns the breadcrumb data for it.
*/
export async function populateBreadcrumbFromComponent(breadcrumb: BreadcrumbComponent) {
  // This is the first manually authored parent from the Builder Editor. We drill down to get the reference input.
  const immediateParent = breadcrumb.component?.options?.breadcrumb;

  // Stupid fix for when the breadcrumb is added but not set/authored correctly.
  if (!immediateParent) {
    return [];
  }

  return populateBreadcrumbFromReference(immediateParent);
}

export async function populateBreadcrumbFromReference(parent: BreadcrumbInputReference) {
  const trail = await getBreadcrumbTrail(parent);

  const breadcrumbs = trail
    .map((element) => ({
      name: element?.data?.name,
      path: element?.data?.path,
    }))
    .reverse() as BreadcrumbItem[];

  return breadcrumbs;
}

/*
  This is used to get the breadcrumb trail for a given BuilderReference.
  
  1) Takes an initial BreadcrumbInputReference.
  2) Gets the current value, then checks (and gets) its parent reference value. 
  3) Recursively calls itself until it finds the root parent.
  4) Returns an array of all parents.
 */
export async function getBreadcrumbTrail(initial: BreadcrumbInputReference, trail: BreadcrumbContent[] = []) {
  /*
    This can either be a populated reference, or an unpopulated reference.

    If it's populated, we can just use the value.
    If it's unpopulated, we need to get call the Builder API to get the content based on the reference ID.
  */

  const current = initial.value || (await getBreadcrumbContent(initial.id));
  const updatedTrail = [...trail, current];
  const nextParent = current?.data?.parent;

  if (nextParent?.id) {
    return getBreadcrumbTrail(nextParent, updatedTrail);
  }

  return updatedTrail;
}
